import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaUsers,
  FaHardHat,
  FaFileInvoiceDollar,
  FaArrowUp,
  FaArrowDown,
  FaPlus,
} from "react-icons/fa";

import { db } from "../../firebase/firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { useClientsStatusMap } from "../../hooks/useClientsStatusMap";

import "./DashHome.css";

function initials(name) {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// Extended to understand the app's real status vocabulary (client
// lifecycle: "In Talk" / "In Progress" / "Completed" / "Rejected") on top
// of the invoice payment states ("Paid" / "Pending") derived below —
// while keeping the exact same chip classes/colors already used
// everywhere else in the app.
function statusClass(status) {
  if (status === "Paid" || status === "Active" || status === "Completed" || status === "In Progress") {
    return "chip chip--green";
  }
  if (status === "Pending" || status === "On Hold" || status === "In Talk") {
    return "chip chip--yellow";
  }
  return "chip chip--red"; // Rejected, Overdue, etc.
}

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const item = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

function DashboardHome() {
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());

  // Single source of truth for clients + status — same realtime hook used
  // by Clients.js / Estimation1.js / Invoice.js / Materials.js, so this
  // page never maintains its own copy of client/status data.
  const { clientsById, clientsLoading } = useClientsStatusMap();

  // Invoices have no shared hook yet, so subscribe the same way
  // useClientsStatusMap already does (onSnapshot on the collection) —
  // reusing the established realtime pattern rather than inventing a new
  // one, and without touching Invoice.js's own fetching logic.
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "invoices"),
      (snapshot) => {
        const list = [];
        snapshot.forEach((docSnap) => list.push({ id: docSnap.id, ...docSnap.data() }));
        setInvoices(list);
        setInvoicesLoading(false);
      },
      (err) => {
        console.error("Failed to subscribe to invoices collection:", err);
        setInvoicesLoading(false);
      },
    );
    return () => unsubscribe();
  }, []);

  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  // ---------------- Formatting helpers ----------------
  // Mirrors Invoice.js's formatRupees/formatDate exactly, so figures read
  // identically everywhere in the app.
  const formatRupees = (value) => {
    const num = Number(value);
    if (!value || Number.isNaN(num)) return "₹ 0";
    return `₹ ${num.toLocaleString("en-IN")}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    try {
      const dateObj = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return dateObj.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  // Read-only display of the project's permanent invoice number — same
  // resolution order as Invoice.js's formatMainInvoiceNumber, but never
  // triggers the lazy backfill transaction (that one-time write stays
  // owned exclusively by the Invoice page).
  const formatInvoiceLabel = (invoice) => {
    if (invoice?.mainInvoiceNumber && String(invoice.mainInvoiceNumber).trim()) {
      return invoice.mainInvoiceNumber;
    }
    return invoice?.id ? `INV-${invoice.id.slice(0, 6).toUpperCase()}` : "-";
  };

  const isThisMonth = (timestamp) => {
    if (!timestamp?.toDate) return false;
    const d = timestamp.toDate();
    const t = new Date();
    return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth();
  };

  const isThisWeek = (timestamp) => {
    if (!timestamp?.toDate) return false;
    const d = timestamp.toDate();
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return d >= weekAgo;
  };

  const clientsList = useMemo(() => Object.values(clientsById), [clientsById]);

  // ---------------- Stat Card 1: Total Clients ----------------
  const totalClients = clientsList.length;
  const newClientsThisMonth = useMemo(
    () => clientsList.filter((c) => isThisMonth(c.createdAt)).length,
    [clientsList],
  );

  // ---------------- Stat Card 2: Active Projects ----------------
  // "In Progress" is the app-wide definition of an active project — the
  // exact same value Clients.js's tabs, Estimation1.js's statusFor(), and
  // Invoice.js's statusFor() already treat as "active". Nothing new is
  // introduced here.
  const activeProjectsList = useMemo(
    () => clientsList.filter((c) => c.status === "In Progress"),
    [clientsList],
  );
  const activeProjects = activeProjectsList.length;
  const newActiveThisMonth = useMemo(
    () => activeProjectsList.filter((c) => isThisMonth(c.createdAt)).length,
    [activeProjectsList],
  );

  // ---------------- Stat Card 3: Pending Invoices ----------------
  // Identical calculation to Invoice.js's totalOutstanding.
  const totalOutstanding = useMemo(
    () => invoices.reduce((sum, inv) => sum + (Number(inv.balanceAmount) || 0), 0),
    [invoices],
  );
  const outstandingAddedThisWeek = useMemo(
    () =>
      invoices
        .filter((inv) => isThisWeek(inv.createdAt))
        .reduce((sum, inv) => sum + (Number(inv.balanceAmount) || 0), 0),
    [invoices],
  );

  const STATS = useMemo(
    () => [
      {
        label: "Total Clients",
        value: String(totalClients),
        delta: `+${newClientsThisMonth} this month`,
        up: newClientsThisMonth > 0,
        icon: FaUsers,
        accent: "blue",
      },
      {
        label: "Active Projects",
        value: String(activeProjects),
        delta: `+${newActiveThisMonth} this month`,
        up: newActiveThisMonth > 0,
        icon: FaHardHat,
        accent: "yellow",
      },
      {
        label: "Pending Invoices",
        value: formatRupees(totalOutstanding),
        delta: `+${formatRupees(outstandingAddedThisWeek)} this week`,
        up: outstandingAddedThisWeek > 0,
        icon: FaFileInvoiceDollar,
        accent: "orange",
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [totalClients, newClientsThisMonth, activeProjects, newActiveThisMonth, totalOutstanding, outstandingAddedThisWeek],
  );

  // ---------------- Recent Invoices table ----------------
  const recentInvoices = useMemo(() => {
    return [...invoices]
      .sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return bTime - aTime;
      })
      .slice(0, 5)
      .map((inv) => ({
        key: inv.id,
        number: formatInvoiceLabel(inv),
        client: inv.clientName || "-",
        amount: formatRupees(inv.finalEstimation),
        // Derived from the same balanceAmount ledger Invoice.js already
        // maintains — there is no separate "payment status" field stored
        // anywhere, so this is computed rather than duplicated.
        status: Number(inv.balanceAmount) > 0 ? "Pending" : "Paid",
        date: formatDate(inv.createdAt),
      }));
  }, [invoices]);

  // ---------------- Client Overview ----------------
  const recentClients = useMemo(() => {
    return [...clientsList]
      .sort((a, b) => {
        const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return bTime - aTime;
      })
      .slice(0, 4);
  }, [clientsList]);

  return (
    <motion.div initial="hidden" animate="show" variants={container} className="home">
      {/* hero */}
      <motion.div variants={item} className="home-hero">
        <div>
          <span className="eyebrow">SITE OVERVIEW</span>
          <h1>{greeting}, Administrator.</h1>
          <p>
            Here's where every client, invoice and material stands today,{" "}
            {now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}.
          </p>
        </div>

        <div className="hero-actions">
          <button className="btn btn--primary" onClick={() => navigate("/dashboard/clients")}>
            <FaPlus /> New Client
          </button>
        </div>
      </motion.div>

      {/* stats */}
      <motion.div variants={item} className="stats-grid">
        {STATS.map(({ label, value, delta, up, icon: Icon, accent }) => (
          <div key={label} className={`stat-card corner-marks accent-${accent}`}>
            <div className="stat-top">
              <div className="stat-icon">
                <Icon />
              </div>
              <span className={`stat-delta ${up ? "is-up" : "is-down"}`}>
                {up ? <FaArrowUp /> : <FaArrowDown />} {delta}
              </span>
            </div>
            <h3>{value}</h3>
            <p>{label}</p>
          </div>
        ))}
      </motion.div>

      {/* main grid */}
      <div className="main-grid">
        <motion.div variants={item} className="panel corner-marks invoices-panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">BILLING</span>
              <h2>Recent Invoices</h2>
            </div>
            <button className="btn btn--ghost btn--sm" onClick={() => navigate("/dashboard/invoice")}>
              View all
            </button>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Client</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {invoicesLoading ? (
                  <tr>
                    <td colSpan={5} className="muted">
                      Loading invoices...
                    </td>
                  </tr>
                ) : recentInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="muted">
                      No Invoices Found
                    </td>
                  </tr>
                ) : (
                  recentInvoices.map((inv) => (
                    <tr key={inv.key}>
                      <td className="mono">{inv.number}</td>
                      <td>{inv.client}</td>
                      <td className="mono">{inv.amount}</td>
                      <td>
                        <span className={statusClass(inv.status)}>{inv.status}</span>
                      </td>
                      <td className="muted">{inv.date}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div variants={item} className="panel corner-marks clients-panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">RELATIONSHIPS</span>
              <h2>Client Overview</h2>
            </div>
          </div>

          <div className="client-list">
            {clientsLoading ? (
              <div className="muted">Loading clients...</div>
            ) : recentClients.length === 0 ? (
              <div className="muted">No Clients Found</div>
            ) : (
              recentClients.map((c) => (
                <div key={c.id} className="client-row">
                  <div className="client-avatar">{initials(c.name)}</div>
                  <div className="client-info">
                    <strong>{c.name || "-"}</strong>
                    <span>{c.projectType || "No project type set"}</span>
                  </div>
                  <span className={statusClass(c.status)}>{c.status || "In Talk"}</span>
                </div>
              ))
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default DashboardHome;